import React from 'react';
import { Button, Box, Stack, Typography, Checkbox } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitActivityAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { useSelector } from 'react-redux';

export const ActivityForm = props => {
  const { data, submitCallback, handleClose, allResorts, allSeasons, allTypes, allPeriods, allCategories } = props;
  const { module } = useSelector(state => state.app);
  const { loading, mutate } = useMutation(submitActivityAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste')
    // type: Yup.string().required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    resorts: data ? data.resorts : [],
    seasons: data ? data.seasons : [],
    periods: data ? data.periods : [],
    type: data ? data.type : 'entertainment',
    gross_value: data ? data.gross_value : '0.00',
    description: data ? data.description || '' : '',
    default_load_rate: data ? data.default_load_rate : 0,
    gross_value_per_person: data ? data.gross_value_per_person : '0.00',
    show_on_form: data ? data.show_on_form : true,
    category: data ? data.category : null
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano zajęcie' : 'Dodano nowe zajęcie', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego zajęcia');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto zajęcie ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć zajęcia ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa zajęcia"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Kategoria</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="category"
              name="category"
              options={allCategories}
              value={formik.values.category}
              onChange={formik.handleChange} />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Typ</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="type"
              name="type"
              options={allTypes}
              value={formik.values.type}
              onChange={e => {
                formik.setFieldValue('type', e.target.value);
                formik.setFieldValue('gross_value', '0.00');
              }} />
          </Stack>}
          {(module !== 'wyjazdy_rodzinne' && formik.values.type !== 'additionally_paid') &&
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość (zł)</Typography>
            <DebounceInput
              disabled={!['paintball_game', 'educational_trip', 'optional'].includes(formik.values.type) }
              sx={{ flex: '1 1 90%' }}
              name="gross_value"
              id="gross_value"
              type="text"
              placeholder="wartość promocji"
              value={formik.values.gross_value}
              onChange={formik.handleChange}
              error={formik.touched.gross_value && Boolean(formik.errors.gross_value)}
              helperText={formik.touched.gross_value && formik.errors.gross_value}
              debounceTimeout={300}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>}
          {(module == 'wyjazdy_rodzinne' || formik.values.type === 'additionally_paid') &&
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość (zł) za 1 uczestnika</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="gross_value_per_person"
              id="gross_value_per_person"
              type="text"
              placeholder="wartość"
              value={formik.values.gross_value_per_person}
              onChange={formik.handleChange}
              error={formik.touched.gross_value_per_person && Boolean(formik.errors.gross_value_per_person)}
              helperText={formik.touched.gross_value_per_person && formik.errors.gross_value_per_person}
              debounceTimeout={300}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ośrodki</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="resorts"
              name="resorts"
              options={allResorts}
              value={formik.values.resorts}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezony</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="seasons"
              name="seasons"
              options={allSeasons}
              value={formik.values.seasons}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Terminy</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="periods"
              name="periods"
              options={allPeriods}
              value={formik.values.periods}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Opis</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              id="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              multiline
              rows={8}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Pokazuj na formularzu</Typography>
            <Box sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }} display="flex" justifyContent="flex-start">
              <Checkbox
                id="show_on_form"
                name="show_on_form"
                checked={formik.values.show_on_form}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Domyślny stopień natężenia </Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              id="default_load_rate"
              value={formik.values.default_load_rate}
              onChange={formik.handleChange}
            />
          </Stack>}
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={loading}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć zajęcie?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten zajęcie?
      </ConfirmDialog>
    </>

  );
};
